import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, FileText, Mic2 } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 14 — Script Builder demo.
 * Green-themed (matches the core stack palette).
 *
 * Drop a static screenshot at /app/frontend/public/script-editor.png and it
 * appears full-bleed inside the right-hand frame.
 */

const SCRIPT_EDITOR_SRC = "/script-editor.png";
const GREEN = "#96C11F";
const GREEN_GLOW = "rgba(150,193,31,0.18)";
const GREEN_TINT = "rgba(150,193,31,0.10)";

const beats = [
  { icon: Sparkles, label: "AI-generated, on-brand scripts in seconds" },
  { icon: Mic2, label: "Presenter-ready, voice-ready, consistent output" },
];

export default function Scene14_HowItWorks() {
  const [imageOk, setImageOk] = useState(true);
  return (
    <SceneShell variant="ink" align="left" data-testid="scene-how-it-works">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            "radial-gradient(ellipse at 75% 50%, rgba(150,193,31,0.14) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* Left — the explanation */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 kn-eyebrow"
          >
            <span style={{ color: GREEN }}>How does it work?</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="kn-display text-4xl sm:text-5xl lg:text-6xl text-white mt-6"
            data-testid="how-it-works-title"
          >
            <span style={{ color: GREEN }}>Script Builder.</span>
            <br />
            Tours + Property Updates.
          </motion.h2>

          {/* Two key beats */}
          <ul className="mt-10 space-y-4">
            {beats.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.li
                  key={b.label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: 0.8 + i * 0.22,
                    duration: 0.7,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="flex items-start gap-4 text-base sm:text-lg text-white/85"
                  data-testid={`hiw-beat-${i}`}
                >
                  <span
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: GREEN_GLOW, color: GREEN }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="leading-relaxed">{b.label}</span>
                </motion.li>
              );
            })}
          </ul>

          {/* AI-generated callout (occupies the slot the old quote had) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 inline-flex items-center gap-4 rounded-2xl px-6 py-5 glass"
            style={{
              background: GREEN_TINT,
              border: `1px solid ${GREEN}55`,
              boxShadow: `0 0 40px ${GREEN}22`,
            }}
            data-testid="hiw-callout"
          >
            <span
              className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
              style={{ background: GREEN_GLOW, color: GREEN }}
            >
              <Sparkles className="w-5 h-5" />
            </span>
            <div>
              <div
                className="text-[10px] uppercase tracking-[0.32em]"
                style={{ color: GREEN }}
              >
                AI-generated
              </div>
              <div
                className="kn-display text-lg sm:text-xl text-white"
                style={{ fontWeight: 700 }}
              >
                On-brand · structured · in seconds
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right — script editor screenshot · keeps native aspect ratio */}
        <div className="lg:col-span-7 relative">
          <motion.figure
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl overflow-hidden"
            style={{
              aspectRatio: "1500 / 804",
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${GREEN}33`,
              boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 60px ${GREEN}1f`,
            }}
            data-testid="hiw-screenshot"
          >
            {imageOk ? (
              <img
                src={SCRIPT_EDITOR_SRC}
                alt="NicheCom Script Builder editor screenshot"
                className="absolute inset-0 w-full h-full object-contain"
                onError={() => setImageOk(false)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="text-[11px] tracking-[0.32em] uppercase"
                  style={{ color: "rgba(255,255,255,0.45)" }}
                >
                  Drop screenshot at /public/script-editor.png
                </div>
              </div>
            )}
          </motion.figure>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ delay: 1.6, duration: 0.8 }}
            className="mt-3 flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase justify-end"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            <FileText className="w-3 h-3" style={{ color: GREEN }} />
            <span>Script Builder · live editor</span>
          </motion.div>
        </div>
      </div>
    </SceneShell>
  );
}
